import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { evSearchPageClickthrough } from 'events/search';
import { Loader } from 'components/Loader';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import InlineProductCardAd from 'components/common/InlineProductCardAd';
import { getHeartProps } from 'helpers/HeartUtils';
import { getLowStockLabelStatus } from 'helpers/cardUtils';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import InlineAdSlot from 'components/search/InlineAdSlot';

import css from 'styles/components/search/products.scss';

const MAX_ANIMATION_FRAMES = 9;

export const Products = ({
  products,
  onProductClicked,
  filters,
  heartsData = {},
  productClass,
  products: {
    list,
    allProductsCount,
    inlineRecos,
    msftResults,
    trustedRetailers,
    isLoading,
    oosMessaging
  },
  inlineBannerData,
  trackMicrosoftAdImpressions,
  isCustomer,
  heartProduct,
  unHeartProduct,
  toggleHeartingLoginModal,
  makeScrollButton,
  pixelServerHost,
  hydraColorSwatches,
  crossSiteRecos,
  isLoadingSymphony,
  triggerAssignment
}) => {
  const { testId, marketplace: { features: { showRatings }, search: { msaMelodyImageParams, hasMicrosoftAds, hasCrossSiteSearches, hasStyleRoomFlag } } } = useMartyContext();
  const microsoftAdFrame = useRef();
  const [animationTimerIndex, setAnimationTimerIndex] = useState(0);
  const { page, term } = filters;
  const { impressionUrl } = msftResults;

  useEffect(() => {
    const { list } = products;
    // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
    // of re-renders or lazyloading, which will tick every 800ms
    let intervalId;
    const startAnimationTimer = () => {
      intervalId = setInterval(() => {
        setAnimationTimerIndex(index => {
          let newIndex = index + 1;
          // Reset the cycle
          if (newIndex > MAX_ANIMATION_FRAMES) {
            newIndex = 0;
          }
          return newIndex;
        });
      }, 800);
    };
    list.some(item => item.animationImages?.length) && startAnimationTimer(); // if we find a product that has animation images, start timer
    return () => clearInterval(intervalId);
  }, [products]);

  /**
   * Here, we fire "impression" events when the component mounts to the screen
   * regardless if it is in-view, it's hidden via CSS (mobile screen), and whether or not the user is in msft
   */

  useEffect(() => {
    if (impressionUrl) {
      trackMicrosoftAdImpressions({ url: impressionUrl });
    }
  }, [impressionUrl, trackMicrosoftAdImpressions]);

  const makeCardClick = (product, index) => {
    const { heartsList = {} } = heartsData;
    const { productId, styleId, isLowStock } = product;
    return e => {
      const position = index + 1;
      const eventPayload = {
        pageNumber: page + 1,
        pageResultNumber: position,
        product,
        numberOfHearts: heartsList[styleId],
        isLowStock
      };

      onProductClicked(position, productId, styleId, e);
      track(() => ([
        evSearchPageClickthrough, eventPayload
      ]));
    };
  };

  const makeProducts = () => {
    const {
      hearts
    } = heartsData;

    if (allProductsCount > 0) {
      const combinedProducts = hasCrossSiteSearches ? [ ...trustedRetailers, ...list] : [ ...list];

      const heartsProps = getHeartProps({
        trackEvent,
        hearts,
        isCustomer,
        products,
        heartProduct,
        toggleHeartingLoginModal,
        unHeartProduct,
        isDisplayCount: true,
        hasHearting: true
      }, {
        heartEventName: 'TE_SEARCH_PRODUCT_HEART',
        unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART'
      });
      const heartsInfo = {
        heartsData: heartsProps,
        isCustomer,
        products,
        heartProduct,
        unHeartProduct,
        toggleHeartingLoginModal
      };

      const allProductCards = combinedProducts.map((v, i) => {
        const isLowStock = getLowStockLabelStatus({ ...v, hasStyleRoomFlag });
        v.isLowStock = isLowStock;
        const product = {
          ...v,
          index: i
        };
        const dataTestId = trustedRetailers[i]?.isTrustedRetailer ? 'searchResultsCrossSiteProduct' : 'searchResult';
        return <ColorSwatchWrapper
          {...product}
          heartsInfo={heartsInfo}
          hydraColorSwatches={hydraColorSwatches}
          key={`${product.styleId}-${product.colorId}`}
          testId={testId(dataTestId)}
          animationTimerIndex={animationTimerIndex}
          msaImageParams={msaMelodyImageParams}
          onClick={makeCardClick(v, i)}
          link={product.productSeoUrl || product.productUrl || null}
          showRatings={showRatings}
        />;
      });

      if (inlineBannerData?.placement) {
        const placement = parseInt(inlineBannerData.placement, 10);
        const adIndex = placement - 1;
        const canRenderAd = !isNaN(placement) && placement > 0 && allProductCards.length + 1 >= placement && inlineBannerData?.heading && inlineBannerData?.src && inlineBannerData?.mobilesrc;

        canRenderAd && allProductCards.splice(adIndex, 0, <InlineProductCardAd key="searchInlineHero" term={term} {...inlineBannerData} />);
      }

      let productCardsCount = 0;

      if (allProductsCount) {
        productCardsCount = hasCrossSiteSearches ? trustedRetailers.length + list.length : list.length;
      }

      return (
        <div id="products" data-test-id={testId('productCardContainer')} className={cn(css.products, productClass)}>
          {allProductCards}
          <InlineAdSlot
            trackMicrosoftAdImpressions={trackMicrosoftAdImpressions}
            makeCardClick={makeCardClick}
            msftResults={msftResults}
            msaImageParams={msaMelodyImageParams}
            heartsInfo={heartsInfo}
            inlineRecos={inlineRecos}
            filters={filters}
            crossSiteRecos={crossSiteRecos}
            isLoadingSymphony={isLoadingSymphony}
            productCardsCount={productCardsCount}
            triggerAssignment={triggerAssignment} />
        </div>
      );
    }
    return null;// no results view handled by NoSearchResults.js
  };

  // render
  return (
    <div id="searchPage" className={'searchPage'}>
      <h2>Search Results</h2>
      {oosMessaging && <p className={css.oosMessaging} data-test-id={testId('oosMessage')}>{oosMessaging}</p>}
      {hasMicrosoftAds && pixelServerHost && <iframe
        className={css.misad}
        frameBorder="0"
        title="misad"
        ref={microsoftAdFrame}
        src={`https://${pixelServerHost}/ads/microsoft-ads.html`} />}
      {isLoading ? <Loader /> : makeProducts()}
      {makeScrollButton && makeScrollButton()}
    </div>
  );
};

Products.displayName = 'Products';

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
